import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Eye, EyeOff, ArrowLeft, Sparkles, Check, Users, Globe, Zap } from 'lucide-react';
import { toast } from 'sonner';
import logo from '../assets/NTZLOGOnew.png';
import ReCAPTCHA from 'react-google-recaptcha';
import { registerSchema, validate } from '../lib/validation';

const RECAPTCHA_SITE_KEY = '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';
const BENEFITS = [
  { icon: Zap, text: '100 free credits to start, no card needed' },
  { icon: Users, text: 'Access to free-tier AI agents instantly' },
  { icon: Globe, text: 'Deploy across 15+ industries on day one' },
];

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const recaptchaRef = useRef(null);
  const { register } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  useEffect(() => { const t = setTimeout(() => setVisible(true), 60); return () => clearTimeout(t); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors({});
    const result = validate(registerSchema, { name, email, password });
    if (!result.success) {
      setFieldErrors(result.errors);
      const first = Object.values(result.errors)[0];
      if (first) toast.error(first);
      return;
    }
    if (!captchaToken) { toast.error('Please complete the CAPTCHA'); return; }
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Registration failed';
      if (msg === 'Access pending admin review') {
        toast.info('Your request has been sent to admin for review. You will receive an invite email once approved.', { duration: 6000 });
        // Stay on the page but show success state
        setTimeout(() => navigate('/login'), 4000);
      } else if (msg === 'Email already registered') {
        toast.error('This email is already registered. Please login instead.');
      } else {
        toast.error(msg);
      }
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    }
  };

  const inputStyle = {
    border: isDark ? '1.5px solid rgba(100,160,255,0.18)' : '1.5px solid rgba(0,0,0,0.1)',
    background: isDark ? '#0f1729' : '#fafafa',
    color: isDark ? '#f0f6fc' : '#0f172a',
  };
  const handleFocus = e => {
    e.target.style.border = '1.5px solid #F87217';
    e.target.style.boxShadow = '0 0 0 3px rgba(248,114,23,0.1)';
    e.target.style.background = isDark ? '#162040' : '#fff';
  };
  const handleBlur = e => {
    e.target.style.border = isDark ? '1.5px solid rgba(100,160,255,0.18)' : '1.5px solid rgba(0,0,0,0.1)';
    e.target.style.boxShadow = 'none';
    e.target.style.background = isDark ? '#0f1729' : '#fafafa';
  };

  return (
    <div className="min-h-screen flex">
      {/* ── Left: Form Panel ─────────────────────────────────── */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 sm:px-8 md:px-14 lg:px-20 py-10 lg:py-0 min-h-screen overflow-y-auto" style={{ background: isDark ? '#0a0f1e' : '#fff' }}>
        <Link
          to="/"
          className="flex items-center gap-2 mb-10 text-sm hover:text-slate-800 group w-fit transition-colors"
          style={{
            color: isDark ? '#8b949e' : '#64748b',
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(-10px)',
            transition: 'opacity 0.4s, transform 0.4s',
          }}
        >
          <ArrowLeft className="w-4 h-4 transition-transform duration-200 group-hover:-translate-x-1" />
          Back to Home
        </Link>

        <div
          className="max-w-sm w-full mx-auto"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.5s ease 0.05s, transform 0.5s ease 0.05s',
          }}
        >
          <div className="flex items-center gap-2 mb-8">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#F87217,#FF9A4E)' }}>
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold" style={{ color: isDark ? '#f0f6fc' : '#0f172a' }}>Multi-Agentic</span>
          </div>

          <h1 className="text-3xl font-bold mb-2" style={{ color: isDark ? '#f0f6fc' : '#0f172a', fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.02em' }}>
            Create your account
          </h1>
          <p className="mb-8 text-sm" style={{ color: isDark ? '#8b949e' : '#64748b' }}>Start automating with AI agents today</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div className="space-y-1.5">
              <label htmlFor="name" className="block text-sm font-semibold" style={{ color: isDark ? '#c9d1d9' : '#334155' }}>Full Name</label>
              <input
                id="name"
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full h-12 px-4 rounded-xl text-sm outline-none transition-all duration-200"
                style={inputStyle}
                onFocus={handleFocus}
                onBlur={handleBlur}
                data-testid="register-name-input"
              />
              {fieldErrors.name && <p className="text-xs mt-1" style={{ color: '#ef4444' }}>{fieldErrors.name}</p>}
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <label htmlFor="email" className="block text-sm font-semibold" style={{ color: isDark ? '#c9d1d9' : '#334155' }}>Work Email</label>
              <input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full h-12 px-4 rounded-xl text-sm outline-none transition-all duration-200"
                style={inputStyle}
                onFocus={handleFocus}
                onBlur={handleBlur}
                data-testid="register-email-input"
              />
              {fieldErrors.email && <p className="text-xs mt-1" style={{ color: '#ef4444' }}>{fieldErrors.email}</p>}
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label htmlFor="password" className="block text-sm font-semibold" style={{ color: isDark ? '#c9d1d9' : '#334155' }}>Password</label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Min. 8 characters"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full h-12 px-4 pr-12 rounded-xl text-sm outline-none transition-all duration-200"
                  style={inputStyle}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {/* Password strength bar */}
              {password.length > 0 && (
                <div className="flex gap-1 mt-1.5">
                  {[1, 2, 3, 4].map(n => (
                    <div
                      key={n}
                      className="h-1 flex-1 rounded-full transition-all duration-300"
                      style={{
                        background: password.length >= n * 3
                          ? n <= 1 ? '#ef4444' : n <= 2 ? '#f59e0b' : n <= 3 ? '#3b82f6' : '#10b981'
                          : '#e2e8f0',
                      }}
                    />
                  ))}
                </div>
              )}
              {fieldErrors.password && <p className="text-xs mt-1" style={{ color: '#ef4444' }}>{fieldErrors.password}</p>}
            </div>

            {/* reCAPTCHA */}
            <div className="flex justify-center mt-1" data-ph-no-record>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={token => setCaptchaToken(token)}
                onExpired={() => setCaptchaToken(null)}
                theme={isDark ? 'dark' : 'light'}
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || !captchaToken}
              className="w-full h-12 rounded-xl text-white font-bold text-sm transition-all duration-200 mt-1"
              style={{
                background: (loading || !captchaToken) ? '#f0a067' : 'linear-gradient(135deg,#F87217,#FF9A4E)',
                boxShadow: (loading || !captchaToken) ? 'none' : '0 6px 20px rgba(248,114,23,0.35)',
                cursor: !captchaToken ? 'not-allowed' : 'pointer',
              }}
              onMouseEnter={e => { if (!loading && captchaToken) { e.currentTarget.style.boxShadow = '0 10px 28px rgba(248,114,23,0.5)'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = (loading || !captchaToken) ? 'none' : '0 6px 20px rgba(248,114,23,0.35)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Creating account...
                </span>
              ) : 'Create Account'}
            </button>
          </form>

          {/* Benefits */}
          <div className="mt-7 space-y-2.5">
            {BENEFITS.map(({ icon: Icon, text }, i) => (
              <div
                key={i}
                className="flex items-center gap-2.5 text-xs text-slate-500"
                style={{ animation: visible ? `fadeUp 0.4s ease ${0.3 + i * 0.1}s both` : 'none' }}
              >
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(248,114,23,0.1)' }}>
                  <Check className="w-3 h-3 text-[#F87217]" />
                </div>
                {text}
              </div>
            ))}
          </div>

          <p className="mt-8 text-center text-sm text-slate-500">
            Already have an account?{' '}
            <Link to="/login" className="text-[#F87217] font-semibold hover:text-[#C65B12] transition-colors">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* ── Right: Visual Panel ───────────────────────────────── */}
      <div
        className="hidden lg:flex w-1/2 relative overflow-hidden items-center justify-center"
        style={{ background: 'linear-gradient(150deg,#0d1a3a 0%,#1a0a00 50%,#0d1a3a 100%)' }}
      >
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 40% 50%,rgba(248,114,23,0.2) 0%,transparent 55%)' }} />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.025) 1px,transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />

        <div className="relative z-10 max-w-xs text-center px-8">
          {/* Floating logo */}
          <div
            className="mx-auto mb-8 flex justify-center"
            style={{
              animation: 'float 5s ease-in-out infinite',
            }}
          >
            <img
              src={logo}
              alt="Logo"
              className="w-50 h-50 object-contain"
            />
          </div>


          {/* Stats */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { value: '30+', label: 'AI Agents' },
              { value: '15+', label: 'Industries' },
              { value: '99.9%', label: 'Uptime' },
              { value: '24/7', label: 'Support' },
            ].map(({ value, label }, i) => (
              <div
                key={i}
                className="py-4 px-3 rounded-xl text-center"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  animation: `scaleIn 0.4s ease ${0.2 + i * 0.08}s both`,
                }}
              >
                <p className="text-xl font-bold" style={{ background: 'linear-gradient(135deg,#F87217,#FF9A4E)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{value}</p>
                <p className="text-xs text-slate-500 mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
